/**
 * FILE: packages/supabase/src/services/analytics.ts
 * PURPOSE: Supabase service layer — database access and business logic.
 * @deprecated Use domain-specific subpaths instead. This file will be removed in Phase 4.
 * AGENT_CONTEXT: All DB access goes through service layer. Call logActivity() on writes.
 * INVARIANTS: Soft-delete filter, phone PK protection, activity logging.
 * LAST_REVIEWED: 2026-05-07
 */


import { today } from '@fintree/utils'
import type { Database } from '../types'
import type { DbClient } from "../dbClient";
import { defaultDbClient } from "../dbClient";

type StudentRow = Database['public']['Tables']['students']['Row']
type ActivityLogRow = Database['public']['Tables']['activity_log']['Row']

export interface DashboardStats {
  totalStudents: number
  activeStudents: number
  enrolledThisMonth: number
  /** Enrollments in the previous calendar month (for MoM hint; not total headcount history). */
  enrolledLastMonth: number
  extensionsThisMonth: number
  /** Active students with valid_till within the next 30 days. */
  expiringIn30: number
  incompleteCount: number
  incompleteMissingName: number
  incompleteMissingCourse: number
  incompleteMissingDate: number
  activeJobs: number
  applicationsThisMonth: number
  placementsThisYear: number
  totalCandidates: number
  placementReadyCount: number
  expiringStudents: StudentRow[]
  recentActivity: ActivityLogRow[]
}

export async function getDashboardStats(client: DbClient = defaultDbClient): Promise<DashboardStats> {
  type DashboardStatsCounts = Omit<DashboardStats, 'expiringStudents' | 'recentActivity'>

  const [{ data: statsData, error: statsError }, expiringStudents, recentActivity] = await Promise.all([
    client.rpc('get_dashboard_stats'),
    getExpiringSoonStudents(10, client),
    getRecentActivity(10, client),
  ])

  if (statsError) throw statsError

  const counts = (statsData ?? {}) as Partial<DashboardStatsCounts>

  return {
    totalStudents: counts.totalStudents ?? 0,
    activeStudents: counts.activeStudents ?? 0,
    enrolledThisMonth: counts.enrolledThisMonth ?? 0,
    enrolledLastMonth: counts.enrolledLastMonth ?? 0,
    extensionsThisMonth: counts.extensionsThisMonth ?? 0,
    expiringIn30: counts.expiringIn30 ?? 0,
    incompleteCount: counts.incompleteCount ?? 0,
    incompleteMissingName: counts.incompleteMissingName ?? 0,
    incompleteMissingCourse: counts.incompleteMissingCourse ?? 0,
    incompleteMissingDate: counts.incompleteMissingDate ?? 0,
    activeJobs: counts.activeJobs ?? 0,
    applicationsThisMonth: counts.applicationsThisMonth ?? 0,
    placementsThisYear: counts.placementsThisYear ?? 0,
    totalCandidates: counts.totalCandidates ?? 0,
    placementReadyCount: counts.placementReadyCount ?? 0,
    expiringStudents,
    recentActivity,
  }
}

export async function getExpiringSoonStudents(limit: number, client: DbClient = defaultDbClient): Promise<StudentRow[]> {
  const currentDay = today()
  const dPlus30 = new Date(`${currentDay}T00:00:00`)
  dPlus30.setDate(dPlus30.getDate() + 30)
  const dayPlus30 = `${dPlus30.getFullYear()}-${String(dPlus30.getMonth() + 1).padStart(2, '0')}-${String(dPlus30.getDate()).padStart(2, '0')}`
  const { data, error } = await client
    .from('students')
    .select('*')
    .is('deleted_at', null)
    .eq('status', 'active')
    .gte('valid_till', currentDay)
    .lte('valid_till', dayPlus30)
    .order('valid_till', { ascending: true, nullsFirst: false })
    .limit(limit)

  if (error) throw error
  return data ?? []
}

export async function getRecentActivity(limit: number, client: DbClient = defaultDbClient): Promise<ActivityLogRow[]> {
  const { data, error } = await client
    .from('activity_log')
    .select('*')
    .not('action', 'eq', 'MARK_SUPPORT_MESSAGES_READ')
    .order('created_at', { ascending: false })
    .limit(limit)

  if (error) throw error
  return data ?? []
}

export interface AnalyticsSnapshot {
  placementsCount: number
  applicationsCount: number
  notificationsUnread: number
}

export interface IncompleteStudentsCount {
  total: number
  missingName: number
  missingCourse: number
  missingDate: number
  totalAmount: number
}

export async function getAnalyticsSnapshot(staffId: string, roleName: string, client: DbClient = defaultDbClient): Promise<AnalyticsSnapshot> {
  const [placementsResult, applicationsResult, notificationsResult] = await Promise.all([
    client.from('applications').select('*', { count: 'exact', head: true }).is('deleted_at', null).eq('placed', true),
    client.from('applications').select('*', { count: 'exact', head: true }).is('deleted_at', null),
    client
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('read', false)
      .or(`for_user_id.eq.${staffId},for_role.eq.${roleName}`),
  ])

  if (placementsResult.error) throw placementsResult.error
  if (applicationsResult.error) throw applicationsResult.error
  if (notificationsResult.error) throw notificationsResult.error

  return {
    placementsCount: placementsResult.count ?? 0,
    applicationsCount: applicationsResult.count ?? 0,
    notificationsUnread: notificationsResult.count ?? 0,
  }
}

export async function getUnreadNotificationsCount(staffId: string, roleName: string, client: DbClient = defaultDbClient): Promise<number> {
  let q = client
    .from('notifications')
    .select('*', { count: 'exact', head: true })
    .eq('read', false)

  if (roleName) {
    q = q.or(`for_user_id.eq.${staffId},for_role.eq.${roleName}`)
  } else {
    q = q.eq('for_user_id', staffId)
  }

  const { count, error } = await q
  if (error) throw error
  return count ?? 0
}

export async function getIncompleteStudentsCount(client: DbClient = defaultDbClient): Promise<IncompleteStudentsCount | null> {
  const { data, error } = await client
    .from('students')
    .select('name, course, enrollment_date, amount')
    .is('deleted_at', null)
    .or('name.is.null,course.is.null,enrollment_date.is.null')

  if (error) throw error
  if (!data) return null

  return {
    total: data.length,
    missingName: data.filter((student) => !student.name).length,
    missingCourse: data.filter((student) => !student.course).length,
    missingDate: data.filter((student) => !student.enrollment_date).length,
    totalAmount: data.reduce((sum, student) => sum + (parseFloat(String(student.amount)) || 0), 0),
  }
}

const ENROLLMENT_TREND_PAGE_SIZE = 1000

function last12MonthKeysAscending(): string[] {
  const keys: string[] = []
  const anchor = new Date()
  anchor.setDate(1)
  for (let back = 11; back >= 0; back -= 1) {
    const d = new Date(anchor.getFullYear(), anchor.getMonth() - back, 1)
    keys.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`)
  }
  return keys
}

export interface MonthlyEnrollmentTrendPoint {
  month: string
  count: number
}

/** Monthly enrollment counts for the last 12 calendar months (oldest -> newest), including zeros for empty months.
 *  Uses enrollment_date (actual enrollment date) rather than created_at (import timestamp).
 */
export async function getMonthlyEnrollmentTrend(client: DbClient = defaultDbClient): Promise<MonthlyEnrollmentTrendPoint[]> {
  const monthKeys = last12MonthKeysAscending()
  const now = new Date()
  const startOfOldestMonth = new Date(now.getFullYear(), now.getMonth() - 11, 1)
  const startOfOldestMonthStr = startOfOldestMonth.getFullYear() + '-' + String(startOfOldestMonth.getMonth() + 1).padStart(2, '0') + '-01'

  const rows: { enrollment_date: string | null }[] = []
  let from = 0
  for (;;) {
    const { data, error } = await client
      .from('enrollments')
      .select('enrollment_date')
      .is('deleted_at', null)
      .gte('enrollment_date', startOfOldestMonthStr)
      .order('enrollment_date', { ascending: true })
      .range(from, from + ENROLLMENT_TREND_PAGE_SIZE - 1)

    if (error) throw error
    if (!data?.length) break
    rows.push(...data)
    if (data.length < ENROLLMENT_TREND_PAGE_SIZE) break
    from += ENROLLMENT_TREND_PAGE_SIZE
  }

  const byMonth = new Map<string, number>()
  for (const row of rows) {
    const dateStr = row.enrollment_date
    if (!dateStr) continue
    const ym = dateStr.slice(0, 7)
    byMonth.set(ym, (byMonth.get(ym) ?? 0) + 1)
  }

  return monthKeys.map((month) => ({ month, count: byMonth.get(month) ?? 0 }))
}

// ============================================================================
// Analytics Refresh v2 — Sales + Admin Insights
// ============================================================================

export interface RevenuePulse {
  revenueThisMonth: number
  revenueLastMonth: number
  enrollmentsThisMonth: number
  enrollmentsLastMonth: number
  avgTicketThisMonth: number
}

export async function getRevenuePulse(client: DbClient = defaultDbClient): Promise<RevenuePulse> {
  const { data, error } = await client.rpc('get_analytics_revenue_pulse')
  if (error) throw error
  return ((data ?? {}) as unknown as RevenuePulse)
}

export interface CourseBreakdownItem {
  course: string
  enrollments: number
  revenue: number
}

export async function getCourseBreakdown(client: DbClient = defaultDbClient): Promise<CourseBreakdownItem[]> {
  const { data, error } = await client.rpc('get_analytics_course_breakdown')
  if (error) throw error
  return ((data ?? []) as unknown as CourseBreakdownItem[])
}

export interface AudienceInsights {
  topCities: { city: string; students: number; enrollments: number; revenue: number }[]
  expBandSplit: { band: string; count: number; pct: number }[]
  programPreference: { program: string; count: number; pct: number }[]
  courseByCity: { city: string; course: string; count: number }[]
}

export async function getAudienceInsights(client: DbClient = defaultDbClient): Promise<AudienceInsights> {
  const { data, error } = await client.rpc('get_analytics_audience_insights')
  if (error) throw error
  return (data ?? {
    topCities: [],
    expBandSplit: [],
    programPreference: [],
    courseByCity: [],
  }) as unknown as AudienceInsights
}

export interface MonthlyEnrollmentTrendV2Point {
  month: string
  CFA_L1: number
  CFA_L2: number
  CFA_L3: number
  FRM_P1: number
  FRM_P2: number
  FM: number
}

export async function getMonthlyEnrollmentTrendV2(client: DbClient = defaultDbClient): Promise<MonthlyEnrollmentTrendV2Point[]> {
  const { data, error } = await client.rpc('get_monthly_enrollment_trend_v2')
  if (error) throw error
  return ((data ?? []) as unknown as MonthlyEnrollmentTrendV2Point[])
}
